// Apple Calendar & Reminders through the native bridge (contract §4, bridge
// v1.3; EventKitBridge.swift). Native app only: off-native every call rejects
// UNAVAILABLE, and nothing here is reachable from Chrome UI.
//
// PRIVACY: this module and every module that imports it must never import a
// network client or make a network call (privacyInvariants.test.ts, rule 4). Apple events and reminders stay on the device; the only thing derived
// from them that ever leaves is busy minutes, via toBusyIntervals.
//
// The bridge does NOT gate Pro. Callers do.

import { bridge, bridgeErrorCode, nativeAppInfo } from '../utils/nativeApp';
import type { CalendarEvent, PrayerKey } from '../types';

/** Contract F8: distinct states, never null for "not asked yet". */
export type EventKitAuthStatus = 'notDetermined' | 'restricted' | 'denied' | 'fullAccess' | 'writeOnly';

const STATUSES: readonly EventKitAuthStatus[] = ['notDetermined', 'restricted', 'denied', 'fullAccess', 'writeOnly'];

/** Unknown values (a newer OS state) read as denied: never assume access. */
export const toAuthStatus = (value: unknown): EventKitAuthStatus =>
  (STATUSES as readonly unknown[]).includes(value) ? (value as EventKitAuthStatus) : 'denied';

export type EventKitDomain = 'calendar' | 'reminders';

const readStatus = async (method: string): Promise<EventKitAuthStatus> => {
  const data = await bridge.call<{ status?: unknown }>(method);
  return toAuthStatus(data?.status);
};

export const getEventKitStatus = (domain: EventKitDomain): Promise<EventKitAuthStatus> =>
  readStatus(`${domain}.getStatus`);

/** Shows the OS prompt when notDetermined; resolves with the resulting status. */
export const requestEventKitAccess = (domain: EventKitDomain): Promise<EventKitAuthStatus> =>
  readStatus(`${domain}.requestAccess`);

/** "iPhone" / "iPad" / "Mac" key for copy (F9), or null on older builds. */
export const deviceIdiom = (): 'phone' | 'pad' | 'mac' | null => {
  const idiom = nativeAppInfo()?.idiom;
  return idiom === 'phone' || idiom === 'pad' || idiom === 'mac' ? idiom : null;
};

// ── Calendar ─────────────────────────────────────────────────────────────────

export interface AppleCalendar {
  id: string;
  name: string;
  color: string | null;
  primary: boolean;
  isRhemn: boolean;
  /** "iCloud", "Gmail", "On My iPhone"… */
  source: string;
}

export const listAppleCalendars = async (): Promise<AppleCalendar[]> => {
  const data = await bridge.call<{ calendars?: AppleCalendar[] }>('calendar.listCalendars');
  return Array.isArray(data?.calendars) ? data.calendars : [];
};

/**
 * Events in [from, to) (≤ 72 h). `calendarIds` omitted = every calendar;
 * `[]` = none. Same shape as the GraphQL CalendarEvent (provider 'apple',
 * accountId 'apple'); the Rhemn calendar and cancelled events are excluded
 * natively.
 */
export const fetchAppleEvents = async (
  from: Date,
  to: Date,
  calendarIds?: string[],
): Promise<CalendarEvent[]> => {
  const params: Record<string, unknown> = { from: from.toISOString(), to: to.toISOString() };
  if (calendarIds) params.calendarIds = calendarIds;
  const data = await bridge.call<{ events?: CalendarEvent[] }>('calendar.events', params);
  return Array.isArray(data?.events) ? data.events.map((e) => ({ ...e, provider: 'apple' as const })) : [];
};

export interface ApplePrayerDay {
  date: string;
  prayers: Array<{ key: PrayerKey; title: string; start: string }>;
}

/** Busy, 20 min, in a "Rhemn" EKCalendar; idempotent per (date, key). */
export const upsertApplePrayerTimes = (input: {
  timeZone: string;
  cityLabel?: string;
  days: ApplePrayerDay[];
}): Promise<{ calendarId: string; writtenThrough: string; eventCount: number }> =>
  bridge.call('calendar.upsertPrayerTimes', { ...input, durationMinutes: 20 });

/** Deletes the Rhemn EKCalendar (every prayer time Rhemn wrote). */
export const removeApplePrayerTimes = async (): Promise<void> => {
  await bridge.call('calendar.removePrayerTimes');
};

// ── Reminders ────────────────────────────────────────────────────────────────

export type ReminderPriority = 'Critical' | 'High' | 'Medium' | 'Low';

export interface AppleReminderList {
  id: string;
  name: string;
  color: string | null;
  isDefault: boolean;
}

export interface AppleReminder {
  id: string;
  listId: string;
  title: string;
  completed: boolean;
  completedAt: string | null;
  /** YYYY-MM-DD, read-only. */
  dueDate: string | null;
  priority: ReminderPriority;
  updatedAt: string;
}

export const listReminderLists = async (): Promise<AppleReminderList[]> => {
  const data = await bridge.call<{ lists?: AppleReminderList[] }>('reminders.listLists');
  return Array.isArray(data?.lists) ? data.lists : [];
};

/** Completed reminders come back only from the last 30 days. */
export const fetchReminders = async (listIds: string[], includeCompleted = true): Promise<AppleReminder[]> => {
  if (listIds.length === 0) return [];
  const data = await bridge.call<{ reminders?: AppleReminder[] }>('reminders.fetch', { listIds, includeCompleted });
  return Array.isArray(data?.reminders) ? data.reminders : [];
};

/** Create (no id) or update. Changing listId moves it. Critical is stored as High. */
export const upsertReminder = async (input: {
  id?: string;
  listId: string;
  title: string;
  completed?: boolean;
  priority?: ReminderPriority;
}): Promise<AppleReminder> => {
  const data = await bridge.call<{ reminder: AppleReminder }>('reminders.upsert', { ...input });
  return data.reminder;
};

export const completeReminder = async (id: string, completed: boolean): Promise<AppleReminder> => {
  const data = await bridge.call<{ reminder: AppleReminder }>('reminders.complete', { id, completed });
  return data.reminder;
};

export const deleteReminder = async (id: string): Promise<void> => {
  await bridge.call('reminders.delete', { id });
};

/** DENIED from a data method: access was revoked in Settings while running. */
export const isEventKitDenied = (err: unknown): boolean => bridgeErrorCode(err) === 'DENIED';
